/**
 * Application-wide constants
 * Centralized, immutable values used across the application.
 * Never hardcode magic strings/numbers in components — reference here.
 */

// ─── Roles ────────────────────────────────────────────────
export const ROLES = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

// ─── Employment ───────────────────────────────────────────
export const EMPLOYMENT_TYPE = {
  PERMANENT_FULL_TIME: 'permanent_full_time',
  PERMANENT_PART_TIME: 'permanent_part_time',
  FIXED_TERM: 'fixed_term',
  CONTRACTOR: 'contractor',
  INTERN: 'intern',
} as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPE)[keyof typeof EMPLOYMENT_TYPE];

export const EMPLOYMENT_STATUS = {
  ACTIVE: 'active',
  ON_LEAVE: 'on_leave',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  ARCHIVED: 'archived',
} as const;

export type EmploymentStatus = (typeof EMPLOYMENT_STATUS)[keyof typeof EMPLOYMENT_STATUS];

export const WORKPLACE_TYPE = {
  ONSITE: 'onsite',
  REMOTE: 'remote',
  HYBRID: 'hybrid',
} as const;

export type WorkplaceType = (typeof WORKPLACE_TYPE)[keyof typeof WORKPLACE_TYPE];

// ─── Contracts ────────────────────────────────────────────
export const CONTRACT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  SUPERSEDED: 'superseded',
  EXPIRED: 'expired',
  TERMINATED: 'terminated',
  ARCHIVED: 'archived',
} as const;

export type ContractStatus = (typeof CONTRACT_STATUS)[keyof typeof CONTRACT_STATUS];

export const AMENDMENT_TYPE = {
  SALARY_CHANGE: 'salary_change',
  ROLE_CHANGE: 'role_change',
  SCHEDULE_CHANGE: 'schedule_change',
  RENEWAL: 'renewal',
  OTHER: 'other',
} as const;

export type AmendmentType = (typeof AMENDMENT_TYPE)[keyof typeof AMENDMENT_TYPE];

// ─── Documents ────────────────────────────────────────────
export const DOCUMENT_STATUS = {
  ACTIVE: 'active',
  SUPERSEDED: 'superseded',
  REVOKED: 'revoked',
  ARCHIVED: 'archived',
} as const;

export type DocumentStatus = (typeof DOCUMENT_STATUS)[keyof typeof DOCUMENT_STATUS];

export const DOCUMENT_CATEGORY = {
  IDENTITY_PROOF: 'identity_proof',
  CV: 'cv',
  CONTRACT: 'contract',
  CONTRACT_AMENDMENT: 'contract_amendment',
  EMPLOYEE_GENERAL: 'employee_general',
  CERTIFICATE: 'certificate',
  OTHER: 'other',
} as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORY)[keyof typeof DOCUMENT_CATEGORY];

export const VISIBILITY_SCOPE = {
  ADMIN_ONLY: 'admin_only',
  EMPLOYEE_VISIBLE: 'employee_visible',
  EMPLOYEE_UPLOADABLE: 'employee_uploadable',
  EMPLOYEE_EDITABLE_METADATA: 'employee_editable_metadata',
} as const;

export type VisibilityScope = (typeof VISIBILITY_SCOPE)[keyof typeof VISIBILITY_SCOPE];

// ─── Invitations ──────────────────────────────────────────
export const INVITATION_STATUS = {
  DRAFT: 'draft',
  INVITED: 'invited',
  DELIVERED: 'delivered',
  BOUNCED: 'bounced',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
} as const;

export type InvitationStatus = (typeof INVITATION_STATUS)[keyof typeof INVITATION_STATUS];

// ─── Attendance ───────────────────────────────────────────
export const SESSION_STATUS = {
  RUNNING: 'running',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CORRECTED: 'corrected',
  FLAGGED: 'flagged',
} as const;

export type SessionStatus = (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS];

export const ATTENDANCE_EVENT_TYPE = {
  DAY_START: 'day_start',
  BREAK_START: 'break_start',
  BREAK_END: 'break_end',
  DAY_END: 'day_end',
  MANUAL_ADD: 'manual_add',
  ADMIN_ADJUST: 'admin_adjust',
} as const;

export type AttendanceEventType = (typeof ATTENDANCE_EVENT_TYPE)[keyof typeof ATTENDANCE_EVENT_TYPE];

export const REVIEW_STATUS = {
  NONE: 'none',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type ReviewStatus = (typeof REVIEW_STATUS)[keyof typeof REVIEW_STATUS];

// ─── Location ─────────────────────────────────────────────
export const LOCATION_CAPTURE_STATUS = {
  GRANTED: 'granted',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
  TIMEOUT: 'timeout',
  UNSUPPORTED: 'unsupported',
} as const;

export type LocationCaptureStatus = (typeof LOCATION_CAPTURE_STATUS)[keyof typeof LOCATION_CAPTURE_STATUS];

export const CAPTURE_TYPE = {
  DAILY_LOGIN: 'daily_login',
  CLOCK_IN: 'clock_in',
  CLOCK_OUT: 'clock_out',
} as const;

export type CaptureType = (typeof CAPTURE_TYPE)[keyof typeof CAPTURE_TYPE];

export const GEOFENCE_MATCH = {
  UNKNOWN: 'unknown',
  INSIDE: 'inside',
  OUTSIDE: 'outside',
  NOT_EVALUATED: 'not_evaluated',
} as const;

export type GeofenceMatch = (typeof GEOFENCE_MATCH)[keyof typeof GEOFENCE_MATCH];

export const LOCATION_SOURCE = {
  BROWSER: 'browser',
  MOBILE_BROWSER: 'mobile_browser',
} as const;

export type LocationSource = (typeof LOCATION_SOURCE)[keyof typeof LOCATION_SOURCE];

export const LOCATION_POLICY_ACTION = {
  ALLOW_WITH_WARNING: 'allow_with_warning',
  ALLOW_AND_FLAG: 'allow_and_flag',
  BLOCK: 'block',
} as const;

export type LocationPolicyAction = (typeof LOCATION_POLICY_ACTION)[keyof typeof LOCATION_POLICY_ACTION];

// ─── Review Items ─────────────────────────────────────────
export const REVIEW_ITEM_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  DISMISSED: 'dismissed',
} as const;

export type ReviewItemStatus = (typeof REVIEW_ITEM_STATUS)[keyof typeof REVIEW_ITEM_STATUS];

export const REVIEW_ITEM_TYPE = {
  DENIED_LOCATION: 'denied_location',
  MISSING_LOCATION: 'missing_location',
  UNUSUAL_DURATION: 'unusual_duration',
  OVERLAPPING_ENTRY: 'overlapping_entry',
  MANUAL_CORRECTION: 'manual_correction',
  MISSING_CLOCK_OUT: 'missing_clock_out',
  OUT_OF_POLICY: 'out_of_policy',
} as const;

export type ReviewItemType = (typeof REVIEW_ITEM_TYPE)[keyof typeof REVIEW_ITEM_TYPE];

// ─── Field Permissions ────────────────────────────────────
export const FIELD_PERMISSION_LEVEL = {
  VIEW: 'view',
  EDIT: 'edit',
  PROPOSE_CHANGE: 'propose_change',
  NO_ACCESS: 'no_access',
} as const;

export type FieldPermissionLevel = (typeof FIELD_PERMISSION_LEVEL)[keyof typeof FIELD_PERMISSION_LEVEL];

// ─── Change Requests ──────────────────────────────────────
export const CHANGE_REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
} as const;

export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUS)[keyof typeof CHANGE_REQUEST_STATUS];

// ─── Pagination ───────────────────────────────────────────
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 100,
  EMPLOYEE_LIST_SIZE: 25,
  DOCUMENT_LIST_SIZE: 20,
  AUDIT_LOG_SIZE: 50,
} as const;

// ─── File Uploads ─────────────────────────────────────────
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  ALLOWED_MIME_TYPES: [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ] as readonly string[],
  ALLOWED_EXTENSIONS: ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.doc', '.docx'] as readonly string[],
} as const;

// ─── Date/Time ────────────────────────────────────────────
export const DATE_FORMAT = {
  DISPLAY: 'dd MMM yyyy',
  DISPLAY_WITH_TIME: 'dd MMM yyyy HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  DATE_ONLY: 'yyyy-MM-dd',
  TIME_ONLY: 'HH:mm',
  TIME_WITH_SECONDS: 'HH:mm:ss',
} as const;

// ─── API ──────────────────────────────────────────────────
export const API = {
  DEBOUNCE_MS: 300,
  STALE_TIME_MS: 5 * 60 * 1000,
  CACHE_TIME_MS: 10 * 60 * 1000,
  RETRY_COUNT: 2,
  REQUEST_TIMEOUT_MS: 30_000,
} as const;

// ─── Route Paths ──────────────────────────────────────────
export const ROUTES = {
  LOGIN: '/login',
  ACTIVATE: '/activate',
  RESET_PASSWORD: '/reset-password',
  ACCEPT_INVITE: '/accept-invite',
  UNAUTHORIZED: '/unauthorized',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  EMPLOYEES: '/employees',
  EMPLOYEE_DETAIL: '/employees/:id',
  EMPLOYEE_CREATE: '/employees/new',
  INVITATIONS: '/invitations',
  DOCUMENTS: '/documents',
  CONTRACTS: '/contracts',
  CONTRACT_DETAIL: '/contracts/:id',
  ATTENDANCE_ADMIN: '/attendance',
  REVIEWS: '/reviews',
  AUDIT_LOG: '/audit-log',
  ORGANIZATION: '/organization',
  TENANTS: '/tenants',
  POLICIES: '/policies',
  MY_TIME: '/my-time',
  MY_DOCUMENTS: '/my-documents',
  MY_CONTRACTS: '/my-contracts',
  MY_PROFILE: '/my-profile',
  MY_SETTINGS: '/my-settings',
  PLATFORM_ROOT: '/platform',
  PLATFORM_ORGANIZATIONS: '/platform/organizations',
} as const;

// ─── Storage Buckets ──────────────────────────────────────
export const STORAGE_BUCKETS = {
  DOCUMENTS: 'documents',
  AVATARS: 'avatars',
  CONTRACTS: 'contracts',
  TEMPLATES: 'templates',
} as const;
